import { useEffect, useRef, useCallback } from 'react';

interface UseEventStreamOptions {
	streamId: string;
	onOpen?: (streamId: string) => void;
	onMessage?: (streamId: string, data: string) => void;
	onComplete?: (streamId: string) => void;
	onError?: (error: any) => void;
}

export function useEventStreams() {
	const streamsRef = useRef<Map<string, EventSource>>(new Map());

	const openEventStream = useCallback((url: string, options: UseEventStreamOptions) => {
		const { streamId, onOpen, onMessage, onComplete, onError } = options;

		const eventStream = new EventSource(url, { withCredentials: true });
		streamsRef.current.set(streamId, eventStream);

		const closeEventStream = () => {
			eventStream.close();
			streamsRef.current.delete(streamId);
		};

		eventStream.onopen = () => {
			onOpen?.(streamId);
		};

		eventStream.addEventListener('message', (event: MessageEvent) => {
			onMessage?.(streamId, event.data);
		});

		eventStream.addEventListener('complete', () => {
			onComplete?.(streamId);
			closeEventStream();
		});

		eventStream.onerror = (error: Event) => {
			if (eventStream.readyState === EventSource.CLOSED) {
				onComplete?.(streamId);
			} else {
				onError?.(error);
			}

			closeEventStream();
		};

		return { streamId };
	}, []);

	const getAllEventStreams = useCallback(() => Array.from(streamsRef.current.values()), []);

	const getEventStreamById = useCallback((streamId: string) => streamsRef.current.get(streamId), []);

	const closeAllEventStreams = useCallback(() => {
		streamsRef.current.forEach((es) => es.close());
		streamsRef.current.clear();
	}, []);

	useEffect(
		() => () => {
			streamsRef.current.forEach((es) => es.close());
			streamsRef.current.clear();
		},
		[]
	);

	return { openEventStream, getAllEventStreams, getEventStreamById, closeAllEventStreams };
}
